// Eskom PARCC — in-app notification centre hooks (Lindiwe feedback Jun 23 2026, 4a).
//
// `useMyNotifications` reads the signed-in user's own notifications (the connector
// self-scopes by the verified token), polling so the bell stays current without a
// refresh. `useMarkNotificationRead` / `useMarkAllNotificationsRead` flip the read
// flag (self-scoped at the connector) and refresh the bell.
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../lib/queryClient';
import { queryKeys } from '../lib/queryKeys';
import { fetchMyNotifications, type NotificationRead } from '../lib/db';
import { markNotificationRead, markAllNotificationsRead } from '../lib/dbWrite';
import { useAuth } from '../lib/auth';

/** The caller's own notifications, newest first. Polls every 30s + on tab focus. */
export function useMyNotifications() {
  const { user, role } = useAuth();
  const email = user?.email ?? '';
  return useQuery<NotificationRead[], Error>({
    queryKey: queryKeys.notifications,
    queryFn: fetchMyNotifications,
    enabled: !!role && email.length > 0,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

/** Mark one of the caller's notifications read, then refresh the bell. */
export function useMarkNotificationRead() {
  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
    },
  });
}

/** Mark all of the caller's notifications read, then refresh the bell. */
export function useMarkAllNotificationsRead() {
  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
    },
  });
}
